<template>
  <BaseModal :isVisible="isVisible" @close="$emit('close')" width="5xl">
    <template #header>Process & Analyze Data</template>
    <template #content>
      <p class="text-sm text-gray-500 mb-4">Configure advanced processing settings</p>

      <form id="process-form" @submit.prevent="processData" class="space-y-6">
        <div>
          <!-- Gaps and Non-Wear -->
          <div class="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <h4 class="text-lg font-semibold text-gray-900 mb-2">Gaps and Non-Wear Detection</h4>
                <p class="text-sm text-gray-600 leading-relaxed">Adjust for gaps (missing data) and label non-wear data</p>
              </div>
              <div class="flex items-center ml-6">
                <div class="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span class="text-sm text-green-700 font-medium">Always Enabled</span>
              </div>
            </div>
          </div>

          <!-- Signal Processing -->
          <div class="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <h4 class="text-lg font-semibold text-gray-900 mb-2">Signal Processing</h4>
                <p class="text-sm text-gray-600 leading-relaxed">Identify low-quality data (gaps, non-wear, artifacts) and impute signal, where possible</p>
              </div>
              <div class="flex items-center ml-6">
                <div class="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span class="text-sm text-green-700 font-medium">Always Enabled</span>
              </div>
            </div>
          </div>

          <!-- Day Analysis -->
          <div class="bg-gray-50 p-4 rounded-lg mb-6">
            <div class="flex items-start justify-between mb-4">
              <div class="flex-1">
                <h4 class="text-lg font-medium text-gray-900">Day-Level Analysis</h4>
                <p class="text-sm text-gray-500">Generate daily summary statistics</p>
              </div>
              <div class="flex items-center ml-6 shrink-0">
                <div class="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span class="text-sm text-green-700 font-medium">Always Enabled</span>
              </div>
            </div>

            <div class="mt-4 space-y-4 border-t border-gray-200 pt-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2 flex-1 pr-4">
                  <label class="text-sm font-medium text-gray-700">Drinking-detection model</label>
                  <TeleportTooltip max-width="26rem">
                    <template #trigger>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs cursor-help focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        aria-label="Drinking-detection model info"
                      >?</button>
                    </template>
                    Uses a machine-learning model to predict drinking vs non-drinking days. Forces social days from 6:00 AM to 6:00 AM when enabled. Algorithm was trained using data from a treatment sample; generalizability may be limited.
                  </TeleportTooltip>
                </div>
                <label class="relative inline-flex items-center cursor-pointer shrink-0">
                  <input type="checkbox" v-model="drinkingAlgorithmEnabled" class="sr-only peer">
                  <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2 flex-1 pr-4">
                  <label class="text-sm font-medium text-gray-700" :class="{ 'text-gray-400': drinkingAlgorithmEnabled }">Day Start Hour</label>
                  <TeleportTooltip max-width="26rem">
                    <template #trigger>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs cursor-help focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        :class="{ 'opacity-50': drinkingAlgorithmEnabled }"
                        aria-label="Day start hour info"
                      >?</button>
                    </template>
                    Hour of day to start each day (0 = midnight, 6 = 6 AM, etc.)
                  </TeleportTooltip>
                </div>
                <select
                  v-model.number="processingSettings.day_attrs.day_start_hour"
                  :disabled="drinkingAlgorithmEnabled"
                  class="ml-4 w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <option v-for="hour in 24" :key="hour - 1" :value="hour - 1">{{ hour - 1 }}</option>
                </select>
              </div>
              <p v-if="drinkingAlgorithmEnabled" class="text-xs text-amber-700">
                Locked to 6AM to enable compatibility with drinking-detection model, which was built to assess day-level features from 6AM–6AM
              </p>
            </div>
          </div>

          <!-- Curve Identification -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <div class="flex items-start justify-between mb-4">
              <div class="flex-1">
                <h4 class="text-lg font-medium text-gray-900">Curve Detection</h4>
                <p class="text-sm text-gray-500">Detect and analyze TAC curves</p>
              </div>
              <div class="flex items-center ml-6 shrink-0">
                <div class="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span class="text-sm text-green-700 font-medium">Always Enabled</span>
              </div>
            </div>
            <p v-if="drinkingAlgorithmEnabled" class="text-xs text-amber-700 mb-3">
              Curve settings are locked to model-compatible values while the drinking-detection model is enabled.
            </p>
            <div class="mt-6 space-y-4 border-t border-gray-200 pt-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2 flex-1 pr-4">
                  <label class="text-sm font-medium text-gray-700" :class="{ 'text-gray-400': drinkingAlgorithmEnabled }">Curve Threshold Method</label>
                  <TeleportTooltip max-width="26rem">
                    <template #trigger>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs cursor-help focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        :class="{ 'opacity-50': drinkingAlgorithmEnabled }"
                        aria-label="Curve threshold method info"
                      >?</button>
                    </template>
                    You can either employ a k-means clustering analysis that computes a curve threshold according to this person's data or you can select a curve threshold of your choosing.
                  </TeleportTooltip>
                </div>
                <select
                  v-model="processingSettings.curve_attrs.curve_threshold"
                  :disabled="drinkingAlgorithmEnabled"
                  class="ml-4 w-48 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <option value="auto">Auto (Recommended)</option>
                  <option value="manual">Select Numeric Value</option>
                </select>
              </div>
              <div v-if="processingSettings.curve_attrs.curve_threshold === 'manual'" class="flex items-center justify-between">
                <div class="flex items-center gap-2 flex-1 pr-4">
                  <label class="text-sm font-medium text-gray-700" :class="{ 'text-gray-400': drinkingAlgorithmEnabled }">Numeric Threshold Value (TAC)</label>
                  <TeleportTooltip max-width="26rem">
                    <template #trigger>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs cursor-help focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        :class="{ 'opacity-50': drinkingAlgorithmEnabled }"
                        aria-label="Numeric threshold value info"
                      >?</button>
                    </template>
                    {{ thresholdSensitivityTooltip }}
                  </TeleportTooltip>
                </div>
                <select
                  v-model.number="processingSettings.curve_attrs.manual_threshold"
                  :disabled="drinkingAlgorithmEnabled"
                  class="ml-4 w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <option v-for="value in 16" :key="value + 4" :value="value + 4">{{ value + 4 }}</option>
                </select>
              </div>
              <div v-if="processingSettings.curve_attrs.curve_threshold === 'auto'" class="flex items-center justify-between">
                <div class="flex items-center gap-2 flex-1 pr-4">
                  <label class="text-sm font-medium text-gray-700" :class="{ 'text-gray-400': drinkingAlgorithmEnabled }">Max Threshold Permitted (TAC)</label>
                  <TeleportTooltip max-width="26rem">
                    <template #trigger>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs cursor-help focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        :class="{ 'opacity-50': drinkingAlgorithmEnabled }"
                        aria-label="Max threshold permitted info"
                      >?</button>
                    </template>
                    <p>Maximum allowed threshold; if auto-computed threshold exceeds this value, the curve threshold will be adjusted to this maximum permitted value.</p>
                    <p class="mt-2">{{ thresholdSensitivityTooltip }}</p>
                  </TeleportTooltip>
                </div>
                <select
                  v-model.number="processingSettings.curve_attrs.default_threshold"
                  :disabled="drinkingAlgorithmEnabled"
                  class="ml-4 w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <option v-for="value in 11" :key="value + 4" :value="value + 4">{{ value + 4 }}</option>
                </select>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2 flex-1 pr-4">
                  <label class="text-sm font-medium text-gray-700" :class="{ 'text-gray-400': drinkingAlgorithmEnabled }">Merge Curves Within Duration (Hours)</label>
                  <TeleportTooltip max-width="26rem">
                    <template #trigger>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs cursor-help focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        :class="{ 'opacity-50': drinkingAlgorithmEnabled }"
                        aria-label="Merge curves within duration info"
                      >?</button>
                    </template>
                    Merges nearby detected curves into a single curve when they are separated by no more than this number of hours. Choose "No Merging" to keep each detected curve separate.
                  </TeleportTooltip>
                </div>
                <select
                  v-model.number="processingSettings.curve_attrs.merge_curves_within_duration"
                  :disabled="drinkingAlgorithmEnabled"
                  class="ml-4 w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  <option :value="0">No Merging</option>
                  <option v-for="hour in 6" :key="hour" :value="hour">{{ hour }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

      </form>
    </template>
    <template #footer>
      <div class="flex justify-end space-x-3">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Close
        </button>
        <button
          type="submit"
          form="process-form"
          :disabled="processing"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ processing ? 'Processing...' : 'Process Data' }}
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from './BaseModal.vue'
import TeleportTooltip from './TeleportTooltip.vue'

export default {
  name: 'ProcessDataModal',
  components: {
    BaseModal,
    TeleportTooltip
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    processing: {
      type: Boolean,
      default: false
    },
    processingSettings: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'process'],
  data() {
    return {
      thresholdSensitivityTooltip:
        'Lower thresholds increase sensitivity to detect drinking yet increase risk of detecting noise-related curves that are not associated with alcohol consumption (i.e., lower specificity). If you plan to align curves with self-reported information, lower thresholds are safe. If the goal is to minimize false positive detections, higher thresholds (10 or higher) may be preferred.'
    }
  },
  computed: {
    drinkingAlgorithmEnabled: {
      get() {
        return Boolean(this.processingSettings.day_attrs?.enable_drinking_day_algorithm)
      },
      set(value) {
        if (!this.processingSettings.day_attrs) {
          this.processingSettings.day_attrs = {}
        }
        this.processingSettings.day_attrs.enable_drinking_day_algorithm = value
        if (value) {
          this.enforceDrinkingDayAlgorithmSettings()
        }
      }
    }
  },
  watch: {
    isVisible(open) {
      if (open) {
        this.ensureDayAttrs()
        this.ensureAlwaysOnProcessing()
        if (this.drinkingAlgorithmEnabled) {
          this.enforceDrinkingDayAlgorithmSettings()
        }
      }
    },
    drinkingAlgorithmEnabled(enabled) {
      if (enabled) {
        this.enforceDrinkingDayAlgorithmSettings()
      }
    }
  },
  mounted() {
    this.ensureDayAttrs()
    this.ensureAlwaysOnProcessing()
    if (this.drinkingAlgorithmEnabled) {
      this.enforceDrinkingDayAlgorithmSettings()
    }
  },
  methods: {
    ensureAlwaysOnProcessing() {
      this.processingSettings.analyze_days = true
      this.processingSettings.identify_curves = true
    },
    enforceDrinkingDayAlgorithmSettings() {
      this.ensureAlwaysOnProcessing()
      if (!this.processingSettings.day_attrs) {
        this.processingSettings.day_attrs = {}
      }
      this.processingSettings.day_attrs.day_start_hour = 6
      if (!this.processingSettings.curve_attrs) {
        this.processingSettings.curve_attrs = {}
      }
      this.processingSettings.curve_attrs.curve_threshold = 'auto'
      this.processingSettings.curve_attrs.default_threshold = 15
      this.processingSettings.curve_attrs.merge_curves_within_duration = 2
    },
    ensureDayAttrs() {
      if (!this.processingSettings.day_attrs) {
        this.processingSettings.day_attrs = {
          day_start_hour: 0,
          enable_drinking_day_algorithm: false
        }
      }
    },
    processData() {
      this.ensureAlwaysOnProcessing()
      if (this.drinkingAlgorithmEnabled) {
        this.enforceDrinkingDayAlgorithmSettings()
      }
      this.$emit('process', this.processingSettings)
    }
  }
}
</script>
